import { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * Accessibility preferences for elderly patients:
 * - largeText: when ON, document root gets `large-text` class → CSS up-scales
 *   headings, body copy and large stat displays by ~1.5x.
 * - voiceGuidance: when ON, breathing exercises speak the current phase
 *   ("Inspirez", "Retenez", "Expirez") via Web Speech API.
 * - screenAwake: when ON, exercise modals request a WakeLock to keep the
 *   screen on during a session.
 *
 * All prefs are stored in localStorage (device-local, no backend call).
 */
const KEY = 'respiro_accessibility_prefs_v1';

const DEFAULTS = {
  largeText: false,
  voiceGuidance: false,
  screenAwake: true, // ON by default — reasonable for every patient
};

const readStored = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
};

const AccessibilityContext = createContext({
  ...DEFAULTS,
  setLargeText: () => {},
  setVoiceGuidance: () => {},
  setScreenAwake: () => {},
});

export const AccessibilityProvider = ({ children }) => {
  const [prefs, setPrefs] = useState(readStored);

  // Persist on every change
  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(prefs)); } catch { /* ignore */ }
  }, [prefs]);

  // Reflect largeText on <html> so CSS can scale globally
  useEffect(() => {
    const root = document.documentElement;
    if (prefs.largeText) root.classList.add('large-text');
    else root.classList.remove('large-text');
  }, [prefs.largeText]);

  const setLargeText    = useCallback((v) => setPrefs(p => ({ ...p, largeText: !!v })), []);
  const setVoiceGuidance = useCallback((v) => setPrefs(p => ({ ...p, voiceGuidance: !!v })), []);
  const setScreenAwake  = useCallback((v) => setPrefs(p => ({ ...p, screenAwake: !!v })), []);

  return (
    <AccessibilityContext.Provider value={{
      ...prefs,
      setLargeText,
      setVoiceGuidance,
      setScreenAwake,
    }}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => useContext(AccessibilityContext);
